/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useEffect, useState, useRef} from 'react';
import {useDebounceEffect} from 'ahooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useCustomerMgr, useLocalesMgr} from 'coreHooks';
import {useWishlist, useBasket} from 'brandHooks';
import Customer from 'coreClasses/customer/Customer';

import {requestIdleCallback, cancelIdleCallback} from 'coreUtils/utils';

import {ECustomerAuthType, ICustomerQualifiers} from '../../classes/customer/ICustomer';
import {useGuestWishlistMerge} from './useGuestWishlistMerge';

interface UseShopperOptions {
	currency?: string;
	context?: unknown;
	isReady?: boolean;
}

/**
 * Joins basket and customer hooks into a single hook for initializing their states
 * when the app loads on the client-side. Should only be use at top-level of app.
 * @returns {Object} - customer and basket objects
 */
const useShopper = (opts: UseShopperOptions = {}) => {
	const {currency, context} = opts;
	const {managerRepository} = useGeneralProvider();
	const wishlist = useWishlist();
	const localesMgr = useLocalesMgr();
	const customerMgr = useCustomerMgr();
	const customer: Customer = customerMgr.getCurrentCustomer();
	const basket = useBasket({currency});
	const [needsBasketMerge, setNeedsBasketMerge] = useState(false);
	const [isBasketJustMerged, setIsBasketJustMerged] = useState(false);
	const currentLocale = localesMgr.getCurrentLocale();
	const currentCountryCode = currentLocale.getCountryCode();
	const currentlanguageCode = currentLocale.getLanguageCode();
	const isWishlistEnabledNotAuth = managerRepository
		.getConfigurationsMgr()
		.get('sitePreferences.enableNotAuthenticatedCustomerWishlist');
	const [isReadyFlag, setIsReadyFlag] = useState(opts.isReady);
	const canUpdateBasket =
		isReadyFlag &&
		basket.loaded &&
		customer.isInitialized &&
		customerMgr.isContextAvailable() &&
		customer.customerId === basket?.customerInfo?.customerId;
	const isUpdatingBasket = useRef(false);

	useGuestWishlistMerge({customer, wishlist});

	useEffect(() => {
		if (isReadyFlag) {
			const id = requestIdleCallback(() => customerMgr.loginCustomer());

			return () => cancelIdleCallback(id);
		}
	}, [isReadyFlag]);

	useEffect(() => {
		if (opts.isReady) {
			setIsReadyFlag(opts.isReady);
		}
	}, [opts.isReady]);

	useEffect(() => {
		if (customer.needsBasketMerge) {
			/**
			 * Temporary invalidate the basket till it is merged to the existing one
			 */
			basket.invalidate();
			setNeedsBasketMerge(true);
		}
	}, [customer.needsBasketMerge]);

	useEffect(() => {
		if (needsBasketMerge && customerMgr.isContextAvailable()) {
			basket.mergeBasket().then(() => {
				setNeedsBasketMerge(false);
				setIsBasketJustMerged(true);
			}).catch(() => {
				setNeedsBasketMerge(false);
				setIsBasketJustMerged(false);
			});
		}
	}, [needsBasketMerge, customerMgr.isContextAvailable()]);

	// Handle basket init/updates in response to customer/basket changes.
	useEffect(() => {
		if (
			isReadyFlag
			&& customer.isInitialized
			&& customerMgr.isContextAvailable()
			&& !needsBasketMerge
		) {
			const hasBasket = basket?.loaded;

			/**
			 *  We have a customer but no basket, so we fetch a new or existing basket
			 *  or,
			 *  We have a customer and a basket, but the basket does not belong to this customer,
			 *  so we get their existing basket or create a new one for them
			 */

			if (!hasBasket || (hasBasket && customer?.customerId !== basket.customerInfo?.customerId)) {
				requestIdleCallback(() => basket.getOrCreateBasket());

				return () => null;
			}

			/**
			 * We have a registered customer (customer with email), and we have their basket,
			 * but the email applied to the basket is missing or doesn't match the customer
			 * email. In this case, we update the basket with their email.
			 */
			if (
				hasBasket
				&& customer.isRegistered
				&& customer.email !== basket.customerInfo.email
				&& customer.customerId === basket.customerInfo.customerId
			) {
				basket.updateCustomerInfo({email: customer.email});
			}
		}
	}, [
		customerMgr.isContextAvailable(),
		customer.isInitialized,
		customer.customerId,
		customer.authType,
		needsBasketMerge,
		customer.siteId,
		basket.loaded,
		isReadyFlag
	]);

	useEffect(() => {
		if (canUpdateBasket && !isUpdatingBasket.current) {
			if (
				basket?.shippingAddress?.countryCode !== currentCountryCode
				|| basket?.billingAddress?.countryCode !== currentCountryCode
				|| basket.currency !== currency
				|| isBasketJustMerged
			) {
				isUpdatingBasket.current = true;
				basket.removePaymentInstrument().finally(
					() => basket.setShippingAddress({
						countryCode: currentCountryCode,
					}).then(() => basket.setBillingAddress({
						countryCode: currentCountryCode,
					}))
				).finally(() => {
					const awaitPromise = basket.currency !== currency
						? basket.updateBasketCurrency(currency, basket.basketId)
						: Promise.resolve();

					awaitPromise.then(() => {
						basket.getShippingMethods().then((shippingMethods) => {
							if (
								shippingMethods?.applicableShippingMethods
								&& shippingMethods?.applicableShippingMethods.length > 0
							) {
								const isDefaultShippingMethodApplicable =
									shippingMethods.defaultShippingMethodId &&
									shippingMethods.applicableShippingMethods.find((applicableShippingMethod) => {
										return applicableShippingMethod.id === shippingMethods.defaultShippingMethodId;
									});
								const shippingMethodId = isDefaultShippingMethodApplicable ?
									shippingMethods.defaultShippingMethodId :
									shippingMethods.applicableShippingMethods[0].id;

								basket.setShippingMethod(shippingMethodId);
							}

							isUpdatingBasket.current = false;
						});
					});
				});
			}
		}
	}, [
		currentCountryCode,
		isBasketJustMerged,
		canUpdateBasket,
		basket?.shippingAddress?.countryCode
	]);

	useDebounceEffect(() => {
		if (basket.loaded) {
			basket.fetchPaymentMethodsForBasket();
		}
	}, [basket.lastModified, currentCountryCode, currentlanguageCode], {
		wait: 1500
	});

	useEffect(() => {
		if (customer.isInitialized && (customer.isRegistered || isWishlistEnabledNotAuth)) {
			wishlist.init();
		} else {
			wishlist.reset();
		}
	}, [customer.authType, customer.customerId, customer.siteId]);

	useDebounceEffect(() => {
		if (customer.authType) {
			customerMgr.initCustomerContext(context as ICustomerQualifiers).then(() => {

				if (customer.authType === ECustomerAuthType.REGISTERED) {
					customerMgr.joinToLoyalty();
				}
			});
		}
	}, [
		customer.customerId,
		customer.usid,
		customer.authType,
		context
	], {
		wait: 600
	});

	return {customer, basket, isFirstAuthComplete: customer.isFirstAuthComplete};
};

export default useShopper;
